import { useEffect, useState } from 'react'
import { useMainButton } from '../components/MainButton'
import { TarotCard } from '../components/TarotCard'
import { cardById, cardName, loc } from '../data/cards'
import { GENDERS, signById, toGender } from '../data/signs'
import { spreadById, spreadName } from '../data/spreads'
import { LANG_NAMES, formatDate, spreadsPlural, type TKey } from '../i18n'
import { haptic, telegramUser } from '../lib/telegram'
import { authMode, logout } from '../lib/auth'
import { LANGS } from '../data/cards'
import { enablePush, disablePush } from '../lib/push'
import { api } from '../lib/api'
import { useSirr } from '../store'

const LEVEL_KEY: Record<string, TKey> = {
  novice: 'levelNovice',
  adept: 'levelAdept',
  seer: 'levelSeer',
}

export function Profile({ onNewSpread, onOpenInvites, onLogin }: { onNewSpread: () => void; onOpenInvites: () => void; onLogin: () => void }) {
  const { profile, lang, t, update, setLang, anon } = useSirr()
  const [tab, setTab] = useState<'history' | 'favorites'>('history')
  const [notifyHint, setNotifyHint] = useState(false)
  // Удаление в два нажатия: первое раскрывает предупреждение, второе удаляет.
  const [askDelete, setAskDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(false)

  // Включение уведомлений: в браузере нужна подписка Web Push (разрешение +
  // PushManager), в Telegram достаточно согласия — доставит бот.
  const toggleNotify = async () => {
    haptic.select()
    if (profile.notify) {
      update({ notify: false })
      void api.setNotify(false)
      if (authMode === 'pwa') void disablePush()
      return
    }
    if (authMode === 'pwa') {
      try {
        if ((await enablePush()).ok) {
          update({ notify: true })
          void api.setNotify(true)
          setNotifyHint(false)
        } else {
          setNotifyHint(true)
        }
      } catch {
        setNotifyHint(true)
      }
    } else {
      // Telegram — согласия браузера не нужно, доставит бот.
      update({ notify: true })
      void api.setNotify(true)
    }
  }

  useMainButton({ label: t('newSpread'), onClick: onNewSpread })

  // Реферальная сводка для карточки (полный экран — по кнопке).
  const [ref, setRef] = useState<{ invited: number; earned: number; cap: number } | null>(null)
  useEffect(() => {
    if (anon) return // анониму код не выдаётся: приглашать он не может
    api.referral().then(setRef).catch(() => {})
  }, [anon])

  const user = telegramUser()
  const sign = signById(profile.zodiac)

  return (
    <div className="screen">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          border: '1px solid var(--line)',
          borderRadius: 18,
          padding: 16,
          background: 'linear-gradient(120deg, rgba(45,26,74,.5), rgba(18,10,31,.35))',
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: '1px solid rgba(200,162,78,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: 'var(--gold)',
            background: 'radial-gradient(circle, rgba(58,30,92,.7), rgba(18,10,31,.9))',
          }}
        >
          {sign.glyph}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {authMode === 'pwa' && !user ? (
            // В браузере имя можно задать/изменить — Telegram-имя приходит извне.
            <input
              value={profile.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder={t('loginName')}
              maxLength={60}
              style={{
                fontFamily: 'var(--serif)',
                fontStyle: 'italic',
                fontSize: 20,
                color: 'var(--ink)',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px dashed var(--line)',
                outline: 'none',
                width: '100%',
                padding: '2px 0',
              }}
            />
          ) : (
            <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 20 }}>
              {user?.first_name || profile.name || t('guest')}
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 2 }}>
            {t(LEVEL_KEY[profile.path] ?? 'levelNovice')} · {profile.history.length}{' '}
            {spreadsPlural(profile.history.length, lang)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 22, color: 'var(--gold)' }}>{profile.stars}</div>
          <div style={{ fontSize: 10, color: 'var(--ink-muted)' }}>✦ {t('stars')}</div>
        </div>
      </div>

      {/* Язык интерфейса. Раньше переключался кнопкой в шапке — там он
          перебирался по кругу и не показывал, из чего вообще выбор. */}
      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 16,
          background: 'rgba(30,17,52,.4)',
        }}
      >
        <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600, marginBottom: 10 }}>
          {t('langTitle')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {LANGS.map((code) => {
            const active = lang === code
            return (
              <button
                key={code}
                type="button"
                onClick={() => {
                  haptic.select()
                  setLang(code)
                }}
                style={{
                  flex: 1,
                  padding: '9px 4px',
                  borderRadius: 10,
                  border: `1px solid ${active ? 'var(--line-strong)' : 'var(--line)'}`,
                  background: active ? 'rgba(74,44,20,.45)' : 'transparent',
                  color: active ? 'var(--gold-bright)' : 'var(--ink-soft)',
                  fontFamily: 'var(--sans)',
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                }}
              >
                {LANG_NAMES[code]}
              </button>
            )
          })}
        </div>
      </div>

      {/* Пол. Живёт и здесь, а не только в онбординге: те, кто прошёл его до
          появления этого вопроса, иначе остались бы без ответа навсегда. */}
      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 16,
          background: 'rgba(30,17,52,.4)',
        }}
      >
        <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600, marginBottom: 2 }}>
          {t('genderTitle')}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginBottom: 10 }}>
          {t('genderSub')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {GENDERS.map((g) => {
            const active = profile.gender === g.id
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => {
                  haptic.select()
                  update({ gender: toGender(g.id) })
                }}
                style={{
                  flex: 1,
                  padding: '9px 4px',
                  borderRadius: 10,
                  border: `1px solid ${active ? 'var(--line-strong)' : 'var(--line)'}`,
                  background: active ? 'rgba(74,44,20,.45)' : 'transparent',
                  color: active ? 'var(--gold-bright)' : 'var(--ink-soft)',
                  fontFamily: 'var(--sans)',
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                }}
              >
                {loc(g, lang)}
              </button>
            )
          })}
        </div>
      </div>

      {/* Уведомления: карта дня и акции. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 16,
          background: 'rgba(30,17,52,.4)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{t('notifyTitle')}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 2 }}>
            {notifyHint ? t('notifyBlocked') : t('notifySub')}
          </div>
        </div>
        <button
          role="switch"
          aria-checked={profile.notify}
          onClick={() => void toggleNotify()}
          style={{
            flexShrink: 0,
            width: 46,
            height: 28,
            borderRadius: 999,
            padding: 3,
            border: 'none',
            cursor: 'pointer',
            background: profile.notify ? 'var(--gold-gradient)' : 'rgba(255,255,255,.12)',
            transition: 'background .2s',
            display: 'flex',
            justifyContent: profile.notify ? 'flex-end' : 'flex-start',
          }}
        >
          <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#fff', display: 'block' }} />
        </button>
      </div>

      {/* Пригласи друзей — открывает отдельный экран с прогрессом и «поделиться». */}
      {ref && (
        <button
          onClick={() => {
            haptic.select()
            onOpenInvites()
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            textAlign: 'left',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: '13px 14px',
            marginBottom: 16,
            cursor: 'pointer',
            background: 'linear-gradient(120deg, rgba(45,26,74,.5), rgba(18,10,31,.35))',
          }}
        >
          <span style={{ color: 'var(--gold)', fontSize: 20, flexShrink: 0 }}>✦</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{t('inviteTitle')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 3 }}>
              {t('inviteStats', { n: ref.invited, b: ref.earned })}
            </div>
          </div>
          <span style={{ color: 'var(--gold)', fontSize: 20, flexShrink: 0 }}>›</span>
        </button>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['history', 'favorites'] as const).map((id) => (
          <button
            key={id}
            type="button"
            className="pick"
            data-selected={tab === id}
            style={{ flex: 1, textAlign: 'center', padding: 10, borderRadius: 11, fontSize: 13, fontWeight: 600 }}
            onClick={() => {
              haptic.select()
              setTab(id)
            }}
          >
            {t(id === 'history' ? 'history' : 'favorites')}
          </button>
        ))}
      </div>

      {tab === 'history' &&
        (profile.history.length === 0 ? (
          <Empty text={t('emptyHistory')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {profile.history.map((h) => {
              const def = spreadById(h.spread)
              return (
                <div
                  key={h.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 13,
                    border: '1px solid rgba(200,162,78,.18)',
                    borderRadius: 14,
                    padding: 13,
                    background: 'rgba(30,17,52,.45)',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      border: '1px solid rgba(200,162,78,.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      color: 'var(--gold)',
                    }}
                  >
                    {def.sym}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 16 }}>
                      {spreadName(def, lang)}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: 'var(--ink-muted)',
                        marginTop: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h.cards.map((c) => cardName(cardById(c.id), lang)).join(' · ')}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-faint)', textAlign: 'right', flexShrink: 0 }}>
                    {formatDate(h.ts, lang)}
                  </div>
                </div>
              )
            })}
          </div>
        ))}

      {tab === 'favorites' &&
        (profile.favorites.length === 0 ? (
          <Empty text={t('emptyFavs')} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {profile.favorites.map((id) => {
              const card = cardById(id)
              return (
                <div key={id} style={{ height: 150 }}>
                  <TarotCard faceUp cardId={card.id} sym={card.sym} rank={card.rank} title={cardName(card, lang)} />
                </div>
              )
            })}
          </div>
        ))}

      {/*
        Предложение войти вместо кнопки «Выйти» — тому, кто ещё не назвался.

        Это и есть замена снятой стены: вход никуда не делся, он просто
        перестал быть воротами и стал предложением с понятной выгодой. Текст
        объясняет ровно то, ради чего аккаунт и нужен: пережить переустановку и
        открыться на другом устройстве. Выходить анониму некуда — кнопки
        «Выйти» у него нет, она бы просто стёрла его историю без возврата.
      */}
      {authMode === 'pwa' && anon && (
        <div
          style={{
            margin: '28px auto 0',
            maxWidth: 420,
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: '16px 18px',
            background: 'var(--panel, transparent)',
          }}
        >
          <div style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
            {t('anonTitle')}
          </div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--ink-muted)', marginTop: 6, lineHeight: 1.45 }}>
            {t('anonWhy')}
          </div>
          <button
            onClick={onLogin}
            style={{
              marginTop: 14,
              width: '100%',
              background: 'transparent',
              color: 'var(--ink)',
              border: '1px solid var(--line-strong)',
              borderRadius: 12,
              padding: '11px 20px',
              fontFamily: 'var(--sans)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('enter')}
          </button>
        </div>
      )}

      {/* Выход — только в PWA и только тому, кто вошёл: в Telegram сессией
          управляет сам клиент, а анониму выходить некуда. */}
      {authMode === 'pwa' && !anon && (
        <button
          onClick={() => void logout()}
          style={{
            margin: '28px auto 0',
            display: 'block',
            background: 'transparent',
            color: 'var(--ink-muted)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: '10px 22px',
            fontFamily: 'var(--sans)',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {t('logout')}
        </button>
      )}

      {/*
        Удаление доступно всем, а не только в PWA: требование Google Play
        относится к приложению, а войти в него можно и через Telegram.
        Подтверждение — отдельным нажатием, а не окном браузера: `confirm()`
        в установленной PWA на iOS выглядит чужеродно и умеет не показаться
        вовсе.
      */}
      <div style={{ margin: '18px auto 0', textAlign: 'center' }}>
        {!askDelete ? (
          <button onClick={() => setAskDelete(true)} style={dangerLink}>
            {t('deleteTitle')}
          </button>
        ) : (
          <div style={{ display: 'grid', gap: 8, justifyItems: 'center' }}>
            <div style={{ color: 'var(--ink-muted)', fontSize: 13, maxWidth: 280 }}>{t('deleteSub')}</div>
            <div style={{ fontSize: 14 }}>{t('deleteAsk')}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setAskDelete(false)} style={dangerLink}>
                {t('deleteNo')}
              </button>
              <button
                disabled={deleting}
                onClick={() => {
                  setDeleting(true)
                  void api
                    .deleteAccount()
                    // Данных больше нет, и держать человека в приложении не на
                    // чем: выходим тем же путём, что и по кнопке «Выйти», —
                    // он же чистит локальное хранилище.
                    .then(() => logout())
                    .catch(() => {
                      setDeleting(false)
                      setDeleteError(true)
                    })
                }}
                style={{ ...dangerLink, borderColor: 'var(--bad, #a33)', color: 'var(--bad, #a33)' }}
              >
                {t('deleteYes')}
              </button>
            </div>
            {deleteError && <div style={{ color: 'var(--bad, #a33)', fontSize: 13 }}>{t('deleteFailed')}</div>}
          </div>
        )}
      </div>
    </div>
  )
}

/** Неяркая кнопка-ссылка: удаление не должно выглядеть приглашением. */
const dangerLink: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink-muted)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  padding: '8px 18px',
  fontFamily: 'var(--sans)',
  fontSize: 13,
  cursor: 'pointer',
}

function Empty({ text }: { text: string }) {
  return (
    <p
      className="prose"
      style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--ink-faint)' }}
    >
      {text}
    </p>
  )
}
